import type {
  PartialTaskWithId,
  PatientTaskSearch,
  TaskCreate,
  TaskSearchStatus,
  TaskUpdate,
} from '@quromedical/models';
import { useCallback, useMemo, useState } from 'react';
import { strings } from 'strings';
import { trpc } from 'trpc';

interface useTasksBase {
  addNote: (input: TaskUpdate) => Promise<void>;
  create: (body: TaskCreate) => Promise<void>;
}

export interface useTasksActions extends useTasksBase {
  isMutationLoading: boolean;
}

export interface useTasksProps extends useTasksBase {
  tasks: PartialTaskWithId[];
  isLoading: boolean;
  error: string | undefined;
  invalidate: () => Promise<void>;
  setStatus: React.Dispatch<React.SetStateAction<TaskSearchStatus | undefined>>;
}

export interface usePractitionerTasksProps extends useTasksProps {
  setOwner?: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export const useTasksActions = (onSuccess?: () => Promise<void>) => {
  const { mutate: updateMutation, isPending: isUpdateLoading } =
    trpc.task.update.useMutation();
  const { mutate: createMutation, isPending: isCreateLoading } =
    trpc.task.create.useMutation();

  const addNote = useCallback(
    async (input: TaskUpdate) => {
      updateMutation(input, { onSuccess });
    },
    [updateMutation, onSuccess],
  );

  const create = useCallback(
    async (body: TaskCreate) => {
      createMutation(body, { onSuccess });
    },
    [createMutation, onSuccess],
  );

  const isMutationLoading = isUpdateLoading || isCreateLoading;

  return { isMutationLoading, addNote, create };
};

export const usePractitionerTasks = (): usePractitionerTasksProps => {
  const [status, setStatus] = useState<TaskSearchStatus | undefined>('active');
  const [owner, setOwner] = useState<string | undefined>();

  const {
    error: trpcError,
    isLoading: isLoadingTasks,
    data,
    isRefetching,
    refetch,
  } = trpc.practitioner.getTasks.useQuery(
    {
      status,
      owner,
    },
    { refetchOnWindowFocus: false },
  );

  const onSuccess = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const { isMutationLoading, addNote, create } = useTasksActions(onSuccess);

  const isLoading = useMemo(() => {
    return isLoadingTasks || isRefetching || isMutationLoading;
  }, [isLoadingTasks, isRefetching, isMutationLoading]);

  const tasks = useMemo(
    () => (data?.tasks as PartialTaskWithId[]) || [],
    [data],
  );

  const error = trpcError ? strings.GenericErrorMessage : undefined;

  return {
    tasks,
    isLoading,
    error,
    setStatus,
    setOwner,
    addNote,
    invalidate: onSuccess,
    create,
  };
};

export const usePatientTasks = ({
  subject,
  focus,
}: PatientTaskSearch): useTasksProps => {
  const [status, setStatus] = useState<TaskSearchStatus | undefined>('active');

  const {
    data,
    error: trpcError,
    isLoading: isLoadingTasks,
    isRefetching,
    refetch,
  } = trpc.patient.getTasks.useQuery(
    { status, subject, focus },
    { refetchOnWindowFocus: false },
  );

  const onSuccess = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const { isMutationLoading, addNote, create } = useTasksActions(onSuccess);

  const isLoading = useMemo(() => {
    return isLoadingTasks || isRefetching || isMutationLoading;
  }, [isLoadingTasks, isRefetching, isMutationLoading]);

  const tasks = useMemo(
    () => (data?.tasks as PartialTaskWithId[]) || [],
    [data],
  );

  const error = trpcError ? strings.GenericErrorMessage : undefined;

  return {
    tasks,
    isLoading,
    error,
    addNote,
    invalidate: onSuccess,
    setStatus,
    create,
  };
};
